import { Badge, Button, Stack } from "@mui/material";
import { useGetMyInvitationsQuery } from "Auth/Api/Queries/Invitations/useGetMyInvitationsQuery";
import { useNavigate } from "Navigation/useNavigate";
import { resetUser } from "State/Auth/AuthReducer";
import { useDispatch } from "react-redux";
import { appUrl } from "Utils/UrlUtils";
import { Resources, useResource } from "Translations/Resources";
import { ButtonLink } from "Components/Shared/ButtonLink";
import { useIsUserInMooneLite } from "Hooks/useIsUserInMooneLite";

const RESOURCES = Resources.UserMenu;

type Props = {
  onActionTriggered?: () => void;
};

export const UserActions = ({ onActionTriggered }: Props) => {
  const dispatch = useDispatch();
  const { isLiteTier, isDocExpired, isLiteBecauseOfBank } =
    useIsUserInMooneLite();
  const navigate = useNavigate();
  const { t } = useResource();

  // "Complete registration" only for never-onboarded Lite users. Doc-expired users already finished
  // onboarding — their CTA is rendered elsewhere (MooneLitePopup / DocumentExpirationWarningPopup).
  const showCompleteRegistration = isLiteTier && !isDocExpired;
  // Identity KYC done, bank missing: the wizard has nothing left to offer, the bank connect does.
  const completeRegistrationLink = isLiteBecauseOfBank
    ? appUrl("banking-auth")
    : appUrl("complete-customer-onboarding");
  const completeRegistrationText = isLiteBecauseOfBank
    ? Resources.Components.MooneLitePopup.ConnectBankAccountButton
    : Resources.Components.MooneLitePopup.CompleteRegistrationButton;

  // Show the Invitations entry only when there's at least one pending invitation —
  // keeps the menu uncluttered while making the inbox discoverable the moment something
  // arrives. The server already filters out Expired/Accepted/Declined rows.
  const { data: invitations } = useGetMyInvitationsQuery();
  const pendingInvitationCount = invitations?.items.length ?? 0;
  const hasPendingInvitations = pendingInvitationCount > 0;

  const handleLogout = () => {
    dispatch(
      resetUser({
        onAfterReset: () => navigate(appUrl("sign-in"), { replace: true }),
      }),
    );
  };

  return (
    <Stack gap={1}>
      {showCompleteRegistration && (
        <ButtonLink
          link={completeRegistrationLink}
          text={t(completeRegistrationText)}
          fullWidth
          variant="outlined"
          onClick={onActionTriggered}
          sx={{
            borderColor: "grey.300",
            color: "white",
            bgcolor: "primary.main",
            textTransform: "none",
            fontSize: 14,
            py: 1,
            height: 40,
          }}
        />
      )}
      {hasPendingInvitations && (
        <Badge
          badgeContent={pendingInvitationCount}
          color="primary"
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          sx={{ ".MuiBadge-badge": { top: 6, right: 12 } }}
        >
          <ButtonLink
            link={appUrl("invitations")}
            text={t(RESOURCES.Invitations)}
            fullWidth
            variant="outlined"
            onClick={onActionTriggered}
            sx={{
              borderColor: "grey.300",
              color: "primary.main",
              textTransform: "none",
              fontSize: 14,
              py: 1,
              height: 40,
            }}
          />
        </Badge>
      )}
      <ButtonLink
        link={appUrl("user-account")}
        text={t(RESOURCES.ManageAccount)}
        fullWidth
        variant="outlined"
        sx={{
          borderColor: "grey.300",
          color: "primary.main",
          textTransform: "none",
          fontSize: 14,
          py: 1,
          height: 40,
        }}
      />
      <Button
        fullWidth
        variant="text"
        onClick={handleLogout}
        sx={{
          color: "error.main",
          textTransform: "none",
          fontSize: 14,
          py: 1,
        }}
      >
        {t(RESOURCES.Logout)}
      </Button>
    </Stack>
  );
};
